import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Dialog,
  DialogContent,
  CircularProgress
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchApi } from './fetchApi';

interface ChildArtikelDaten {
  parent_id: string;
  sku: string;
  bezeichnung: string;
  farbe: string;
  groesse: string;
  ean: string;
  asin: string;
  fnsku: string;
  amazon_sku: string;
  shopify_variant_id: string;
  mindestbestand: string;
  bild_url: string;
}

type PresignResponse = {
  key: string;
  uploadUrl: string;
  publicUrl: string;
  error?: string;
};

export default function ChildBearbeiten() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  const [parentInfo, setParentInfo] = useState<string>('');

  const [child, setChild] = useState<ChildArtikelDaten>({
    parent_id: '',
    sku: '',
    bezeichnung: '',
    farbe: '',
    groesse: '',
    ean: '',
    asin: '',
    fnsku: '',
    amazon_sku: '',
    shopify_variant_id: '',
    mindestbestand: '',
    bild_url: ''
  });

  // Bild Upload UI
  const [uploadLaden, setUploadLaden] = useState(false);
  const [uploadFehler, setUploadFehler] = useState<string | null>(null);
  const [bildDialogOpen, setBildDialogOpen] = useState(false);

  // -------------------------------------------------------------
  // Child + Parent laden
  // -------------------------------------------------------------
  useEffect(() => {
    async function ladeArtikel() {
      try {
        if (!id) {
          setFehler('Artikel-ID fehlt in der URL.');
          setLaden(false);
          return;
        }

        const res = await fetchApi(`/api/artikel/${id}`);
        if (!res.ok) throw new Error(`Artikel laden fehlgeschlagen (HTTP ${res.status})`);

        const a = await res.json();

        setChild({
          parent_id: a.parent_id ? String(a.parent_id) : '',
          sku: a.sku || '',
          bezeichnung: a.bezeichnung || '',
          farbe: a.farbe || '',
          groesse: a.groesse || '',
          ean: a.ean || '',
          asin: a.asin || '',
          fnsku: a.fnsku || '',
          amazon_sku: a.amazon_sku || '',
          shopify_variant_id: a.shopify_variant_id || '',
          mindestbestand: a.mindestbestand ? String(a.mindestbestand) : '',
          bild_url: a.bild_url || ''
        });

        if (a.parent_id) {
          const resP = await fetchApi(`/api/artikel/${a.parent_id}`);
          if (resP.ok) {
            const p = await resP.json();
            setParentInfo(`${p.sku} – ${p.bezeichnung}`);
          } else {
            setParentInfo('');
          }
        }

        setLaden(false);
      } catch (err: any) {
        console.error(err);
        setFehler(err?.message || 'Fehler beim Laden des Artikels.');
        setLaden(false);
      }
    }

    ladeArtikel();
  }, [id]);

  // -------------------------------------------------------------
  // FORM-Handling
  // -------------------------------------------------------------
  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setChild(prev => ({ ...prev, [name]: value }));
  }

  // -------------------------------------------------------------
  // Hilfsfunktion: Bild-URL sofort in DB speichern
  // -------------------------------------------------------------
  async function saveBildUrlToDb(artikelId: number, publicUrl: string | null) {
    const resp = await fetchApi(`/api/artikel/${artikelId}/bild`, {
  method: 'PATCH',
  body: JSON.stringify({ bild_url: publicUrl })
});

    // PATCH kann auch mal leeren Body liefern → defensiv lesen
    const data = await resp.json().catch(() => null);

    if (!resp.ok) {
      const msg = data?.error || `Bild-URL speichern fehlgeschlagen (HTTP ${resp.status}).`;
      throw new Error(msg);
    }
  }

  // -------------------------------------------------------------
  // BILD UPLOAD (Presign -> PUT -> PATCH bild_url)
  // -------------------------------------------------------------
  async function handleBildDatei(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] || null;
    e.target.value = ''; // erlaubt gleiche Datei nochmal zu wählen
    if (!file) return;

    setFehler(null);
    setUploadFehler(null);
    setErfolg(null);

    if (!id) {
      setUploadFehler('Artikel-ID fehlt.');
      return;
    }

    const artikelId = Number(id);
    if (!Number.isFinite(artikelId) || artikelId <= 0) {
      setUploadFehler('Artikel-ID ist ungültig.');
      return;
    }

    const okTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      setUploadFehler('Nur JPG/PNG/WebP erlaubt.');
      return;
    }

    const maxBytes = 3 * 1024 * 1024;
    if (file.size > maxBytes) {
      setUploadFehler('Max. 3 MB erlaubt.');
      return;
    }

    setUploadLaden(true);

    try {
      // 1) Presign holen
      const presignResp = await fetchApi('/api/uploads/artikelbild/presign', {
  method: 'POST',
  body: JSON.stringify({
    artikel_id: artikelId,
    content_type: file.type,
    file_size: file.size
  })
});


      const presignData = (await presignResp.json().catch(() => null)) as PresignResponse | null;

      if (!presignResp.ok) {
        throw new Error(presignData?.error || `Presign fehlgeschlagen (HTTP ${presignResp.status}).`);
      }
      if (!presignData?.uploadUrl || !presignData?.publicUrl) {
        throw new Error('Presign-Antwort unvollständig (uploadUrl/publicUrl fehlen).');
      }

      const uploadUrl = presignData.uploadUrl;
      const publicUrl = presignData.publicUrl;

      // 2) PUT Upload nach S3
      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      if (!putResp.ok) {
        // S3 liefert oft XML → trotzdem hilfreich für Konsole
        const txt = await putResp.text().catch(() => '');
        console.error('S3 PUT Fehler:', putResp.status, txt);
        throw new Error(`Upload fehlgeschlagen (HTTP ${putResp.status}).`);
      }

      // 3) sofort DB speichern
      await saveBildUrlToDb(artikelId, publicUrl);

      // 4) UI State aktualisieren
      setChild(prev => ({ ...prev, bild_url: publicUrl }));
      setErfolg('Bild gespeichert.');
    } catch (err: any) {
      console.error(err);
      setUploadFehler(err?.message || 'Bild-Upload fehlgeschlagen.');
    } finally {
      setUploadLaden(false);
    }
  }

  // -------------------------------------------------------------
  // SPEICHERN (Formular)
  // -------------------------------------------------------------
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setErfolg(null);

    if (!id) {
      setFehler('Artikel-ID fehlt.');
      return;
    }

    const payload = {
      sku: child.sku,
      parent_id: child.parent_id ? parseInt(child.parent_id, 10) : null,

      bezeichnung: child.bezeichnung || null,
      farbe: child.farbe || null,
      groesse: child.groesse || null,
      mindestbestand: child.mindestbestand ? parseInt(child.mindestbestand, 10) : null,

      ean: child.ean || null,
      asin: child.asin || null,
      fnsku: child.fnsku || null,
      amazon_sku: child.amazon_sku || null,
      shopify_variant_id: child.shopify_variant_id || null,

      // bleibt drin (ist ok), auch wenn wir das Bild jetzt separat patchen
      bild_url: child.bild_url || null,

      // Felder beim Child IMMER NULL
      beschreibung: null,
      einkaufspreis: null,
      verkaufspreis: null,
      kategorie_id: null,
      lieferant_id: null,
      amazon_product_id: null,
      shopify_product_id: null,
      barcode_intern: null
    };

    try {
      const res = await fetchApi(`/api/artikel/${id}`, {
  method: 'PUT',
  body: JSON.stringify(payload)
});


      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || `Fehler beim Speichern (HTTP ${res.status}).`);
      }

      setErfolg('Änderungen gespeichert.');
    } catch (err: any) {
      console.error(err);
      setFehler(
  err?.message === '401'
    ? 'Session abgelaufen. Bitte neu einloggen.'
    : (err?.message || 'Fehler beim Speichern.')
);

    }
  }

  // -------------------------------------------------------------
  // Render
  // -------------------------------------------------------------
  if (laden) {
    return (
      <Container maxWidth="md">
        <Typography mt={4}>Lade Artikel…</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={3}>
        <Typography variant="h5">Child-Artikel bearbeiten</Typography>
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}
      {uploadFehler && <Alert severity="error" sx={{ mb: 2 }}>{uploadFehler}</Alert>}
      {erfolg && <Alert severity="success" sx={{ mb: 2 }}>{erfolg}</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* Parent-SKU / Bezeichnung (readonly) */}
          <Grid item xs={12}>
            <TextField
              label="Parent"
              fullWidth
              value={parentInfo}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {/* SKU */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Child SKU"
              name="sku"
              fullWidth
              required
              value={child.sku}
              onChange={handleChange}
            />
          </Grid>

          {/* Bezeichnung */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Bezeichnung"
              name="bezeichnung"
              fullWidth
              value={child.bezeichnung}
              onChange={handleChange}
            />
          </Grid>

          {/* Farbe */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Farbe"
              name="farbe"
              fullWidth
              value={child.farbe}
              onChange={handleChange}
            />
          </Grid>

          {/* Größe */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Größe"
              name="groesse"
              fullWidth
              value={child.groesse}
              onChange={handleChange}
            />
          </Grid>

          {/* Mindestbestand */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Mindestbestand"
              name="mindestbestand"
              type="number"
              fullWidth
              value={child.mindestbestand}
              onChange={handleChange}
            />
          </Grid>

          {/* EAN */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="EAN"
              name="ean"
              fullWidth
              value={child.ean}
              onChange={handleChange}
            />
          </Grid>

          {/* ASIN */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="ASIN"
              name="asin"
              fullWidth
              value={child.asin}
              onChange={handleChange}
            />
          </Grid>

          {/* FNSKU */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="FNSKU"
              name="fnsku"
              fullWidth
              value={child.fnsku}
              onChange={handleChange}
            />
          </Grid>

          {/* Amazon SKU */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amazon SKU"
              name="amazon_sku"
              fullWidth
              value={child.amazon_sku}
              onChange={handleChange}
            />
          </Grid>

          {/* Shopify Variant ID */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Shopify Variant ID"
              name="shopify_variant_id"
              fullWidth
              value={child.shopify_variant_id}
              onChange={handleChange}
            />
          </Grid>

          {/* Bild (Upload + Vorschau + URL readonly) */}
          <Grid item xs={12}>
            <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Artikelbild (1 Bild, max. 3 MB, JPG/PNG/WebP)
              </Typography>

              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Button variant="outlined" component="label" disabled={uploadLaden}>
                  Bild auswählen & hochladen
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={handleBildDatei}
                  />
                </Button>

                {uploadLaden && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Upload läuft…</Typography>
                  </Box>
                )}

                {child.bild_url ? (
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.12)',
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                    title="Klicken zum Vergrößern"
                    onClick={() => setBildDialogOpen(true)}
                  >
                    <Box
                      component="img"
                      src={child.bild_url}
                      alt="Artikelbild"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Kein Bild hinterlegt.
                  </Typography>
                )}
              </Box>

              <Box mt={2}>
                <TextField
                  label="Bild URL (automatisch)"
                  fullWidth
                  value={child.bild_url}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Buttons */}
          <Grid item xs={12} display="flex" justifyContent="flex-start" gap={2}>
            <Button variant="contained" color="primary" type="submit">
              Speichern
            </Button>

            <Button variant="outlined" color="primary" onClick={() => navigate('/artikel-liste')}>
              Zurück
            </Button>
          </Grid>

        </Grid>
      </form>

      {/* Bild-Dialog groß */}
      <Dialog open={bildDialogOpen} onClose={() => setBildDialogOpen(false)} maxWidth="lg">
        <DialogContent sx={{ p: 1 }}>
          {child.bild_url && (
            <Box
              component="img"
              src={child.bild_url}
              alt="Artikelbild groß"
              sx={{ width: '100%', height: 'auto', display: 'block', maxWidth: 1100 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
